import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Input } from "../../../components/input/input";
import { Button } from "../../../components/button/button";
import { Spring } from "../../../components/spring/spring";
import { setStatus } from "../../../features/statusReducer";
import { useAnimatedNavigate } from "../../../hooks/useAnimatedNavigate";
import { isValidEmail, normalizeEmail } from "../../../utils/emailValidation";
import styles from "../_styles/authPage.module.css";
import AuthAPI from "../../../api/AuthAPI";
import UserAPI from "../../../api/UserAPI";

import { Text } from "../../../components/text/text";

export const RegisterPage = () => {
    const dispatch = useDispatch();
    const navigate = useAnimatedNavigate();
    const [searchParams] = useSearchParams();
    const { inviteToken: inviteTokenFromPath } = useParams();

    const inviteToken = useMemo(() => {
        return inviteTokenFromPath || searchParams.get("inviteToken") || null;
    }, [inviteTokenFromPath, searchParams]);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [nameError, setNameError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    const [errorText, setErrorText] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => setNameError(false), [name]);
    useEffect(() => setEmailError(false), [email]);
    useEffect(() => setPasswordError(false), [password]);
    useEffect(() => setConfirmPasswordError(false), [confirmPassword]);
    useEffect(() => {
        setErrorText("");
    }, [name, email, password, confirmPassword]);

    const redirectByStatus = useCallback(async () => {
        const status = await UserAPI.fetchUserStatus();
        dispatch(setStatus(status));

        if (status.isSuperAdmin) {
            navigate("/superadmin");
            return;
        }

        if (status.isUser) {
            navigate("/");
            return;
        }

        if (status.isAdmin) {
            navigate("/admin");
            return;
        }

        navigate("/");
    }, [dispatch, navigate]);

    const validate = () => {
        let hasError = false;
        const normalizedEmail = normalizeEmail(email);

        setNameError(false);
        setEmailError(false);
        setPasswordError(false);
        setConfirmPasswordError(false);

        if (name.trim().length < 2) {
            setNameError(true);
            hasError = true;
        }

        if (!isValidEmail(normalizedEmail)) {
            setEmailError(true);
            setErrorText("Введите корректный email");
            hasError = true;
        }

        if (password.length < 6) {
            setPasswordError(true);
            hasError = true;
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError(true);
            hasError = true;
        }

        return hasError;
    };

    const userId = useSelector(state => state.user.userId);
    useEffect(() => {
        const process = async () => {
            try {
                await AuthAPI.acceptInvite(inviteToken);
            } catch (error) {
                const detail = error?.response?.data?.detail;
                if (error?.response?.status !== 409) {
                    setErrorText(detail || "Не удалось привязать аккаунт к кофейне. Обратитесь к администратору.");
                    return;
                }
            }
            await redirectByStatus();
        }

        if (inviteToken && userId) {
            process();
        }
    }, [inviteToken, redirectByStatus, userId]);

    const onSubmit = async (event) => {
        event.preventDefault();

        if (validate()) return;

        setLoading(true);

        try {
            await AuthAPI.register(name.trim(), normalizeEmail(email), password, inviteToken);

            if (inviteToken) {
                try {
                    await AuthAPI.acceptInvite(inviteToken);
                } catch (error) {
                    const detail = error?.response?.data?.detail;
                    if (error?.response?.status !== 409) {
                        setErrorText(detail || "Аккаунт создан, но не удалось привязать к кофейне. Обратитесь к администратору.");
                        return;
                    }
                }
            }

            await redirectByStatus();
        } catch (error) {
            setErrorText(error?.response?.data?.detail || "Не удалось завершить регистрацию");
        } finally {
            setLoading(false);
        }
    };

    const openLogin = () => {
        if (inviteToken) {
            navigate(`/auth/login?inviteToken=${inviteToken}`);
        } else {
            navigate("/auth/login");
        }
    };

    return (
        <>
            <Text as="div" variant="h3" className={styles.title} size={28} weight={700}>Регистрация</Text>

            <form onSubmit={onSubmit} className={styles.form}>
                <Input
                    value={name}
                    setValue={setName}
                    label="Имя"
                    error={nameError}
                />

                <Input
                    value={email}
                    setValue={setEmail}
                    label="Email"
                    placeholder="example@example.com"
                    autoComplete="email"
                    error={emailError}
                />

                <Input
                    value={password}
                    setValue={setPassword}
                    label="Пароль"
                    type="password"
                    error={passwordError}
                />

                <Input
                    value={confirmPassword}
                    setValue={setConfirmPassword}
                    label="Повторите пароль"
                    type="password"
                    error={confirmPasswordError}
                />

                <Spring />

                <Button
                    buttonType="submit"
                    text={loading ? "Создаем аккаунт..." : "Зарегистрироваться"}
                    disabled={loading}
                />

                <Button
                    type="secondary"
                    text="Уже есть аккаунт? Войти"
                    onClick={openLogin}
                    disabled={loading}
                />

                {
                    !!errorText &&
                    <Text as="div" align="center" variant="body" className={styles.errorText} size={14} lineHeight="1.3">
                        {
                            typeof errorText === "string" ? errorText : "Произошла ошибка"
                        }
                    </Text>
                }
            </form>
        </>
    );
}